/* eslint-disable camelcase */
import { node } from "~/util/dom";
import { CONFIG } from "~/config";
import ReportFormField from "./index";
import HistorySuggestItem from "~/es6/history.suggest.item";
import { ICommonOpts } from "~/report/interfaces";
import { store } from "~/state/store";
import { isLocationUsable } from "~/util/validation";
import { _ } from "~/i18n";
import { isPoitypeIdValid } from "../util";

export type IInputType = "text"|"number"|"email";

interface IOpts extends ICommonOpts {
	name: string;
	label?: string;
	sublabel?: string;
	placeholder?: string;
	value?: string;
	suggestData?: any;
	footnote?: string;
	hasSuggest?: boolean;
	setSuggestParams?: (data: any) => void;
	processSuggestData?: (data: any) => void;
	typeSign?: HTMLElement|SVGElement;
	changeSign?: HTMLElement|SVGElement;
	container: HTMLElement;
	validate?: (value: string) => { status: string; message: string; };
	notSelectedError?: string;
	notSuggestedError?: string;
	type?: IInputType;
	addFormFieldInputClass?: boolean;
	customClass?: string;
	isEmail?: boolean;
	onChange: (value: string, second: any) => void;
}

export default class ReportFormFieldInput extends ReportFormField {
	#opts: IOpts;

	#inputNode: HTMLInputElement;

	#messageNode: HTMLElement;

	#value: string;

	#originalValue = "";

	#originalSuggestData: any = null;

	#suggest: SMap.Suggest;

	#suggestData: any;

	#suggestDataOrig: any;

	#suggestItemsCount: number;

	#isOpen: boolean;

	#delay = 1;

	#suggestItems: Array<any>;

	constructor(opts: IOpts) {
		super(opts);
		this.#opts = {
			onChange: () => { /**/ },
			parentName: "",
			name: "",
			label: "",
			sublabel: "",
			placeholder: "",
			value: "",
			suggestData: null,
			footnote: "",
			hasSuggest: false,
			setSuggestParams: () => { /**/ },
			processSuggestData: () => { /**/ },
			typeSign: null,
			changeSign: null,
			container: null,
			validate: null,
			notSelectedError: "",
			notSuggestedError: "",
			type: "text",
			addFormFieldInputClass: false,
			isEmail: false,
			customClass: "",
			...opts,
		};
		this.#isOpen = false;
		this.#suggestData = null;
		this.#suggestDataOrig = this.#opts.suggestData;

		this.#suggestItemsCount = 0;
		this.#suggestItems = [];

		this.#build();
	}

	// eslint-disable-next-line class-methods-use-this
	destroy() {
		//
	}

	get isOpen() {
		return this.#isOpen;
	}

	get inputNode() {
		return this.#inputNode;
	}

	handleEvent(event) {
		if (event.type === "keydown" && event.code === "Escape" && this.#isOpen) {
			event.stopPropagation();
		}

		if (
			this.#suggest && this.#inputNode.value && !this.#isOpen
			&& (event.type === "focus" || event.type === "mousedown")
		) {
			this.#suggest.send();
		}

		if (event.type === "input") {
			this.#setError();
			this.#opts.onChange(this.#inputNode.value, null);
		}

		if (super.container.classList.contains("simple")) {
			if (event.type === "focus") {
				super.container.classList.add("focused");
			} else if (event.type === "blur") {
				super.container.classList.remove("focused");
			}
		}
	}

	#build() {
		const { parentName, name, label, sublabel, placeholder, value, suggestData, footnote, hasSuggest, typeSign, changeSign, type } = this.#opts;

		this.#suggestData = suggestData;
		this.#inputNode = node("input", { type });
		this.#inputNode.id = `${parentName}-${name}-input`;

		let labelId = null;

		if (label || sublabel) {
			const labelNode = node("label", { className: "label" });

			labelNode.id = `${parentName}-${name}-label`;
			labelId = labelNode.id;
			labelNode.innerHTML = `<strong>${label}</strong>${sublabel || ""}`;
			labelNode.setAttribute("for", this.#inputNode.id);
			super.container.append(labelNode, this.#inputNode);
		} else {
			super.container.classList.add("simple");

			if (typeSign) {
				typeSign.classList.add("type-sign", "no-shrink");
				super.container.append(typeSign);
			}
			super.container.append(this.#inputNode);
		}

		if (changeSign) {
			changeSign.classList.add("change-sign", "no-shrink");
			super.container.append(changeSign);
		}

		this.#messageNode = node("label", { className: "message" });
		this.#messageNode.id = `${parentName}-${name}-message`;
		this.#messageNode.setAttribute("for", this.#inputNode.id);

		this.#inputNode.setAttribute("name", name);
		if (labelId) {
			this.#inputNode.setAttribute("aria-labeledby", `${labelId} ${this.#messageNode.id}`);
		}

		if (value) {
			this.#value = value;
			this.#originalValue = value;
			this.#originalSuggestData = suggestData;
			this.#inputNode.value = value;
		}

		if (placeholder) {
			this.#inputNode.setAttribute("placeholder", placeholder);
		}

		if (footnote) {
			super.container.classList.add("with-footnote");
			this.#messageNode.innerHTML = footnote;
		}

		super.container.append(this.#messageNode);

		this.#inputNode.addEventListener("focus", this);
		this.#inputNode.addEventListener("blur", this);


		if (hasSuggest) {
			this.#inputNode.addEventListener("keydown", this);
			this.#inputNode.addEventListener("mousedown", this);
			this.#setSuggest();

			if (this.#inputNode.value) {
				this.#onChange();
			}
		} else {
			this.#inputNode.addEventListener("input", this);

			if (this.#inputNode.value) {
				this.#opts.onChange(this.#inputNode.value, null);
			}
		}

		if (this.#opts.addFormFieldInputClass) {
			this.container.classList.add("form-field-input");
		}

		if (this.#opts.customClass) {
			this.container.classList.add(this.#opts.customClass);
		}

		if (this.#opts.isEmail) {
			this.container.classList.add("email-note2");
			!this.#opts.value && this.#setError();
		}

		// smazeme
		this.#suggestDataOrig = null;
	}

	#setSuggestParams = paramsObj => {
		const state = store.getState();
		const { longitude, latitude, zoom } = state.map;

		paramsObj.lon = longitude;
		paramsObj.lat = latitude;
		paramsObj.zoom = zoom;
		paramsObj.lang = CONFIG.langPriority.join(",");

		if (typeof this.#opts.setSuggestParams === "function") {
			this.#opts.setSuggestParams(paramsObj);
		}

		if (isLocationUsable(state.user.location)) {
			paramsObj.userLat = state.user.location.latitude;
			paramsObj.userLon = state.user.location.longitude;
			paramsObj.userAccuracy = state.user.location.accuracy;
		}
	};

	#setSuggest() {
		this.#suggest = new SMap.Suggest(this.#inputNode, {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			factory: (data, pos) => new HistorySuggestItem(data, pos),
			delay: this.#delay,
			provider: new SMap.SuggestProvider({
				url: `${CONFIG.requestUrl}/suggest/`,
				poiaggUrl: `${CONFIG.requestUrl}/poiagg`,
			}),
		});

		this.#suggest.addListener("suggest", suggestData => {
			this.#value = this.#inputNode.value;

			this.#suggestData = typeof this.#opts.processSuggestData === "function"
				? this.#opts.processSuggestData(suggestData.data)
				: suggestData.data;
		});

		this.#suggest.addListener("enter", () => {
			if (this.#suggestItems.length) {
				const firstItem = this.#suggestItems[0];

				this.#value = firstItem.title;
				this.#inputNode.value = firstItem.title;

				this.#suggestData = typeof this.#opts.processSuggestData === "function"
					? this.#opts.processSuggestData(firstItem)
					: firstItem;

				this.#setError();
				this.#opts.onChange(this.#inputNode.value, this.#suggestData);
			}
		});

		this.#suggest.addListener("request-items", suggestItems => {
			this.#suggestItems = suggestItems;
			this.#suggestItemsCount = suggestItems.length;

			if (!this.#suggestItemsCount) {
				this.#suggestData = null;

				if (!this.#suggestItemsCount) {
					this.#onChange();
				}
			}

			if (this.#isOpen) {
				setTimeout(
					() => { this.#opts.container.dispatchEvent(new Event("resize-content")); },
					this.#delay,
				);
			}
		});

		this.#suggest.addListener("request", () => {
			this.#suggestItemsCount = 0;
		});

		this.#suggest.addListener("open", () => {
			this.#isOpen = true;
			this.#inputNode.classList.add("suggest");
		});

		this.#suggest.addListener("close", () => {
			this.#isOpen = false;
			this.#opts.container.dispatchEvent(new Event("resize-content"));
			this.#inputNode.classList.remove("suggest");
			this.#onChange();
		});

		this.#suggest.getProvider().updateParams(this.#setSuggestParams);

		// vypneme def. suggest abort, nedelalo to dobrotu
		this.#suggest.abort = () => {};
	}

	#onChange() {
		if (this.#inputNode.value !== this.#value && !this.#suggestDataOrig) {
			this.#suggestData = null;
		}
		this.#setError();
		this.#opts.onChange(this.#inputNode.value, this.#suggestData);
	}

	#setError() {
		if (this.#opts.hasSuggest) {
			if (this.#originalSuggestData !== this.#suggestData) {
				super.container.classList.add("changed");
			} else if (super.container.classList.contains("changed")) {
				super.container.classList.remove("changed");
			}

			if (this.#suggestData) {
				let isError = false;

				if (this.#opts.name === "category" && this.#suggestData !== true) {
					isError = !isPoitypeIdValid(this.#suggestData.poiTypeId);
				}

				if (isError) {
					super.container.classList.remove("valid");
					super.container.classList.add("invalid");
					this.#messageNode.innerText = _("report.invalidPoiTypeId");
				} else {
					super.container.classList.remove("invalid");
					super.container.classList.add("valid");
					this.#messageNode.innerText = "";
				}
			} else {
				super.container.classList.remove("valid");

				if (this.#inputNode.value.length) {
					super.container.classList.add("invalid");

					if (this.#suggestItemsCount) {
						this.#messageNode.innerText = this.#opts.notSelectedError;
					} else {
						this.#messageNode.innerText = this.#opts.notSuggestedError;
					}
				}
			}
		} else {
			const { value } = this.#inputNode;

			if (this.#originalValue !== value) {
				super.container.classList.add("changed");
			} else if (super.container.classList.contains("changed")) {
				super.container.classList.remove("changed");
			}

			if (typeof this.#opts.validate === "function") {
				const report = this.#opts.validate(this.#inputNode.value);

				if (report.status === "valid") {
					super.container.classList.remove("invalid");
					super.container.classList.add("valid");
					this.#messageNode.innerText = "";
				} else if (report.status === "invalid") {
					super.container.classList.remove("valid");
					super.container.classList.add("invalid");
					this.#messageNode.innerText = report.message;
				} else {
					super.container.classList.remove("valid");
					super.container.classList.remove("invalid");
				}
			}
		}
	}

	setValue(value: string, suggestData: any) {
		this.#value = value;
		this.#inputNode.value = value;
		this.#suggestData = suggestData;
		this.#setError();
	}

	hide() {
		super.hide();
		this.#opts.onChange("", null);
	}

	show() {
		super.show();
		this.#opts.onChange(this.#inputNode.value, this.#suggestData);
	}
}
